import React, { useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { EAggregationType } from '@unfoldrtech/portal-mic'
import {
  ETimeBucketType,
  EParamTimeWindow,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { IReportFiltersProps } from '../../models/interfaces'
import { TReportDateRange } from '../../models/types'
import {
  dayjsFormat,
  getDateRanges,
  getEndOfDay,
  getStartOfDay,
  getTodayInMs,
} from '../../utils/helpers'
import {
  FilterContainer,
  Container,
  StyledRangePicker,
  StyledAntDropdown,
  StyledAntMenu,
  IconArrowDownSmall,
  SVGContainer,
  IconDownload,
} from '../Global'
import I18NText from '../I18NText'
import {
  selectCampaignsChartReportingFilters,
  selectReportingFilters,
  setAggregationTypeV3,
  setTimeProperties,
  setTimeWindow,
} from '../../store/reportingFilters'
import { useFeatureFlags } from '../../hooks/useFeatureFlags'
import { PORTAL_FF_REPORTING_EXPORT_EXCEL } from '../../utils/constants'

function Filters({
  onDateChangeFn,
  onExportData,
  isDateRangeFilterDisabled,
  isExportDisabled,
}: IReportFiltersProps) {
  const intl = useIntl()
  const dispatch = useDispatch()

  const featureFlags = useFeatureFlags()

  const { startDate, endDate, IsReportingV3 } = useSelector(
    selectReportingFilters
  )
  const { aggregationType } = useSelector(selectCampaignsChartReportingFilters)

  const [dateRanges, setDateRanges] = useState<TReportDateRange>(
    getDateRanges()
  )

  let selectedDateRange: Array<Dayjs> = [dayjs(startDate), dayjs(endDate)]

  const isDateDisabled = useMemo(
    () => (date: dayjs.Dayjs) => {
      const endOfDay = getEndOfDay(getTodayInMs())

      if (date && endOfDay && date.valueOf() > endOfDay.valueOf()) {
        return true
      }

      return false
    },
    [dateRanges]
  )

  const onDateChange = (startDayjs: Dayjs, endDayjs: Dayjs) => {
    const mappedStartDate = getStartOfDay(startDayjs).toJSON()
    const mappedEndDate = getEndOfDay(endDayjs).toJSON()
    dispatch(setTimeWindow({ timeWindow: EParamTimeWindow.Custom }))
    dispatch(
      setTimeProperties({
        startDate: mappedStartDate,
        endDate: mappedEndDate,
        aggregationType: aggregationType || EAggregationType.Daily,
      })
    )
  }

  // Makes the changes backwards compatible with v1 reporting pages
  // New reporting page versions should ideally not pass date change function
  const onDateChangeCallback = onDateChangeFn ?? onDateChange

  return (
    <>
      <Container display="flex">
        <FilterContainer display="flex" alignItems="center">
          <StyledRangePicker
            border={{ style: 'hidden' }}
            height="100%"
            placeholder={[
              intl.formatMessage({
                id: 'from',
              }),
              intl.formatMessage({
                id: 'to',
              }),
            ]}
            disabledDate={isDateDisabled}
            value={[dayjs(startDate), dayjs(endDate)]}
            onChange={(dateRangeValues) => {
              if (dateRangeValues && dateRangeValues[0] && dateRangeValues[1]) {
                selectedDateRange = [dateRangeValues[0], dateRangeValues[1]]
              }
            }}
            onOpenChange={(isOpen) => {
              if (!isOpen) {
                setTimeout(() => {
                  onDateChangeCallback(
                    selectedDateRange[0],
                    selectedDateRange[1]
                  )
                }, 300)
              }
              if (isOpen) {
                setDateRanges(getDateRanges())
              }
            }}
            ranges={dateRanges}
            disabled={isDateRangeFilterDisabled}
            format={dayjsFormat}
            data-testid="range-picker"
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement as HTMLElement
            }
          />
        </FilterContainer>
      </Container>
      {IsReportingV3 ? (
        <Container padding="0 0 0 var(--margin-default)">
          <FilterContainer>
            <StyledAntDropdown
              overlay={
                <StyledAntMenu>
                  {aggregationType !== ETimeBucketType.Daily ? (
                    <StyledAntMenu.Item
                      key="daily"
                      onClick={() => {
                        dispatch(
                          setAggregationTypeV3({
                            aggregationType: ETimeBucketType.Daily,
                          })
                        )
                      }}
                    >
                      <I18NText id="daily" />
                    </StyledAntMenu.Item>
                  ) : null}
                  {aggregationType !== ETimeBucketType.Weekly ? (
                    <StyledAntMenu.Item
                      key="weekly"
                      onClick={() => {
                        dispatch(
                          setAggregationTypeV3({
                            aggregationType: ETimeBucketType.Weekly,
                          })
                        )
                      }}
                    >
                      <I18NText id="weekly" />
                    </StyledAntMenu.Item>
                  ) : null}
                  {aggregationType !== ETimeBucketType.Monthly ? (
                    <StyledAntMenu.Item
                      key="monthly"
                      onClick={() => {
                        dispatch(
                          setAggregationTypeV3({
                            aggregationType: ETimeBucketType.Monthly,
                          })
                        )
                      }}
                    >
                      <I18NText id="monthly" />
                    </StyledAntMenu.Item>
                  ) : null}
                  {aggregationType !== ETimeBucketType.Quarterly ? (
                    <StyledAntMenu.Item
                      onClick={() => {
                        dispatch(
                          setAggregationTypeV3({
                            aggregationType: ETimeBucketType.Quarterly,
                          })
                        )
                      }}
                    >
                      <I18NText id="quarterly" />
                    </StyledAntMenu.Item>
                  ) : null}
                  {aggregationType !== ETimeBucketType.Yearly ? (
                    <StyledAntMenu.Item
                      key="yearly"
                      onClick={() => {
                        dispatch(
                          setAggregationTypeV3({
                            aggregationType: ETimeBucketType.Yearly,
                          })
                        )
                      }}
                    >
                      <I18NText id="yearly" />
                    </StyledAntMenu.Item>
                  ) : null}
                </StyledAntMenu>
              }
              disabled={false}
            >
              <Container
                preset="paragraph opensans semibold"
                padding="0 var(--padding-default)"
                height="100%"
                display="flex"
                alignItems="center"
              >
                <>
                  {intl.formatMessage({
                    id: `${aggregationType}`,
                  })}
                </>
                <SVGContainer
                  preset="paragraph small"
                  margin="0 0 0 var(--margin-default)"
                >
                  <IconArrowDownSmall />
                </SVGContainer>
              </Container>
            </StyledAntDropdown>
          </FilterContainer>
        </Container>
      ) : null}
      <Container padding="0 0 0 var(--margin-default)">
        <FilterContainer>
          <StyledAntDropdown
            overlay={
              <StyledAntMenu>
                {Boolean(featureFlags[PORTAL_FF_REPORTING_EXPORT_EXCEL]) && (
                  <StyledAntMenu.Item
                    key="xlsx"
                    onClick={() => {
                      if (onExportData) {
                        onExportData({ startDate, endDate }, 'xlsx')
                      }
                    }}
                  >
                    <I18NText id="export.data.xlsx" />
                  </StyledAntMenu.Item>
                )}
                <StyledAntMenu.Item
                  key="csv"
                  onClick={() => {
                    if (onExportData) {
                      onExportData({ startDate, endDate }, 'csv')
                    }
                  }}
                >
                  <I18NText id="export.data.csv" />
                </StyledAntMenu.Item>
              </StyledAntMenu>
            }
            disabled={isExportDisabled}
          >
            <Container
              preset="paragraph opensans semibold"
              padding="0 var(--padding-default)"
              height="100%"
              display="flex"
              alignItems="center"
              data-testid="export-data-dropdown"
            >
              {IsReportingV3 ? (
                <SVGContainer>
                  <IconDownload />
                </SVGContainer>
              ) : (
                <I18NText id="export" />
              )}
              <SVGContainer
                preset="paragraph small"
                margin="0 0 0 var(--margin-default)"
              >
                <IconArrowDownSmall />
              </SVGContainer>
            </Container>
          </StyledAntDropdown>
        </FilterContainer>
      </Container>
    </>
  )
}

export default Filters
